import { useState } from "react"
import Dropzone from "react-dropzone"
import { MultipleFieldErrors, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { ErrorMessage } from "@hookform/error-message"
import { Banner } from "@/components/ui/banner"
import { FormError, FormField } from "@/components/ui/form"
import { mbToBytes } from "@/lib/mb-to-bytes"
import { cn } from "@/lib/utils"
import { UPLOAD_CONFIGS } from "@/modules/upload/constants/upload-configs"
import { FilePreviews } from "@/modules/upload/file-previews"
import { UploadFormData } from "@/modules/upload/schema/upload-schema"

type DropzoneSectionProps = {
  isUploading: boolean
  errorFileName?: string
  onFileDialogOpen: () => void
  onDragEnter: () => void
  onRemoveSuccessful?: (file: File) => void
  onDropzoneChange?: () => void
}

const DropzoneSection = ({
  isUploading,
  errorFileName,
  onFileDialogOpen,
  onDragEnter,
  onRemoveSuccessful,
  onDropzoneChange,
}: DropzoneSectionProps) => {
  const {
    t,
    i18n: { exists },
  } = useTranslation()
  // Used to reset fileRejections, due to no built-in dropzone method exposed
  const [dropzoneKey, setDropzoneKey] = useState(0)
  const form = useFormContext<UploadFormData>()

  const {
    control,
    formState: { errors, isSubmitting },
  } = form

  const handleErrors = (messages: MultipleFieldErrors | undefined) => {
    if (!messages) return null
    return Object.keys(messages).reduce<React.JSX.Element[]>((errorComponents, type) => {
      const message = messages[type]
      if (type !== "too_big" && typeof message === "string") {
        errorComponents.push(<FormError key={type}>{message && exists(message) ? t(message) : message}</FormError>)
      }
      return errorComponents
    }, [])
  }

  return (
    <FormField
      name="files"
      control={control}
      render={({ field: { value, onChange } }) => {
        return (
          <Dropzone
            key={dropzoneKey}
            multiple
            maxSize={mbToBytes(UPLOAD_CONFIGS.MAX_FILE_SIZE_IN_MB)}
            accept={UPLOAD_CONFIGS.ACCEPT_FILE_TYPES}
            onFileDialogOpen={onFileDialogOpen}
            onDragEnter={onDragEnter}
            onDrop={(acceptedFiles) => {
              const mappedFiles = acceptedFiles.map((file) =>
                Object.assign(file, {
                  preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : "",
                }),
              )
              onChange([...value, ...mappedFiles])
              onDropzoneChange?.()
            }}>
            {({ getRootProps, getInputProps, isDragActive, isFileDialogActive, fileRejections }) => {
              const hasError = errors.files || fileRejections.length > 0
              return (
                <section>
                  <div
                    data-testid="file-upload-drop-zone"
                    {...getRootProps({
                      className: cn(
                        "grid items-center cursor-pointer h-24 rounded-xl p-6 border border-border border-dashed hover:bg-primary-surface-subdued transition-all",
                        (isDragActive || isFileDialogActive) && "bg-primary-surface-subdued border-on-primary-surface",
                        hasError && "border-critical bg-critical-surface-subdued",
                        value.length >= UPLOAD_CONFIGS.MAX_FILE && "hidden",
                        isUploading && "pointer-events-none",
                      ),
                    })}>
                    <input {...getInputProps()} />
                    <div className="text-center">
                      {isDragActive ? (
                        <p className="text-on-primary-surface text-sm">{t("patient.uploadFiles.dropFilesToUpload")}</p>
                      ) : (
                        <>
                          <p className={cn("text-on-surface text-sm", hasError && "text-on-critical-surface")}>
                            {t("patient.uploadFiles.addFiles")}
                          </p>
                          <p className={cn("text-on-surface-subdued text-xs", hasError && "text-on-critical-surface")}>
                            {t("patient.uploadFiles.dragYourFilesHere")}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <ErrorMessage errors={errors} name="files" render={({ messages }) => handleErrors(messages)} />
                  {fileRejections && fileRejections.length > 0 && (
                    <FormError>{t("patient.uploadFiles.incompatibleFile")}</FormError>
                  )}
                  {value && value.length > 0 && (
                    <FilePreviews
                      value={value}
                      isSubmitting={isSubmitting}
                      isUploading={isUploading}
                      errorFileName={errorFileName}
                      onRemove={(file) => {
                        const newValue = [...value]
                        newValue.splice(value.indexOf(file), 1)
                        onChange(newValue)
                        onDropzoneChange?.()
                        onRemoveSuccessful?.(file)
                        setDropzoneKey(newValue.length)
                      }}
                    />
                  )}
                  {(errors.files?.types?.too_big || value.length === UPLOAD_CONFIGS.MAX_FILE) && (
                    <Banner type="inline" className="mt-4">
                      {t("patient.uploadFiles.reachedMax", {
                        max: UPLOAD_CONFIGS.MAX_FILE,
                      })}
                    </Banner>
                  )}
                </section>
              )
            }}
          </Dropzone>
        )
      }}
    />
  )
}

export { DropzoneSection }
